import { useContext, useEffect, useRef, useState } from 'react';
import { LinkField, RichText, RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';

import { icon_chevron_right } from '@gn-audio/icons';
import { Button, TrackingContext } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';
import {
  AfterSubmitFormMessage,
  CrmFormEntity,
  FormMessageType,
  GtmEventType,
} from '@gn-audio/jss-core';

import { mapSharedPropertiesToFields } from '../../common/getters';
import { withTheme } from '../../common/jabraTheme/withTheme';
import { handleFormMessage } from '../../common/utils/formHelpers';
import { ComponentProps } from '../../lib/component-props';

type SimpleFormWrapper = {
  content: RichTextField;
  link: LinkField;
  formIframeSrc: LinkField;
};

type SimpleFormWrapperProps = ComponentProps & {
  fields: SimpleFormWrapper;
};

const SimpleFormWrapper: React.FC<SimpleFormWrapperProps> = ({ fields }) => {
  const formWrapper = mapSharedPropertiesToFields<SimpleFormWrapper>(fields);

  const iframe = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<string | undefined>(undefined);
  const [iframeId, setIframeId] = useState();
  const { gtmEvent } = useContext(TrackingContext);

  useEffect(() => {
    const setHeight = (event: MessageEvent) => {
      return setIframeHeight(`${event.data.height}px`);
    };
    const setId = (event: MessageEvent) => {
      return setIframeId(event.data.formId);
    };
    const redirectToUrl = (event: MessageEvent) => (window.location.href = event.data.url);
    const emitGtmEvent = (event: MessageEvent<AfterSubmitFormMessage>) => {
      if (event.data.crmEntity !== CrmFormEntity.Lead) return;

      const { crmEntity, crmCampaignId, crmCampaignName, crmTopic } = event.data;
      gtmEvent({
        event: GtmEventType.JssLeadFormSubmitted,
        crmEntity,
        crmCampaignId,
        crmCampaignName,
        crmTopic,
      });
    };

    const formHeightHandler = handleFormMessage(FormMessageType.SetHeight, setHeight);
    const formIdHandler = handleFormMessage(FormMessageType.SetFormId, setId);
    const formRedirectHandler = handleFormMessage(FormMessageType.OnRedirect, redirectToUrl);
    const formSubmitHandler = handleFormMessage(FormMessageType.AfterSubmit, emitGtmEvent);

    window.addEventListener('message', formHeightHandler);
    window.addEventListener('message', formIdHandler);
    window.addEventListener('message', formRedirectHandler);
    window.addEventListener('message', formSubmitHandler);
    return () => {
      window.removeEventListener('message', formHeightHandler);
      window.removeEventListener('message', formIdHandler);
      window.removeEventListener('message', formRedirectHandler);
      window.removeEventListener('message', formSubmitHandler);
    };
  }, [gtmEvent]);

  return (
    <div className="bg-theme container-fluid flex flex-wrap py-8 lg:py-12 xl:py-16">
      <div className="mb-5 px-10 lg:basis-2/5 lg:px-14 lg:py-5 xl:basis-6/12 xl:px-20 xl:py-12">
        <RichText className="mb-6 gap-6 lg:mb-6 xl:mb-3" field={formWrapper.content} />
        {!!formWrapper.link?.value?.href && (
          <Button
            size="m"
            variant="secondary"
            className="mt-6 flex items-center"
            iconPlacement="right"
            icon={<JabraIcon className="h-4 w-4" Icon={icon_chevron_right} />}
            asLink
            href={formWrapper.link.value.href}
            target={formWrapper.link.value.target}
            title={formWrapper.link.value.title}
          >
            {formWrapper.link.value.text}
          </Button>
        )}
      </div>
      <div className="basis-full px-4 lg:basis-3/5 lg:px-14 xl:basis-6/12 xl:px-20">
        <iframe
          id={iframeId}
          ref={iframe}
          src={formWrapper.formIframeSrc.value.href}
          title={formWrapper.formIframeSrc.value.title}
          height={iframeHeight}
          className="w-full overflow-hidden rounded-3xl"
        />
      </div>
    </div>
  );
};
export default withTheme()(SimpleFormWrapper);
